import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  VStack,
  HStack,
  Button,
  Text,
  Badge,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Progress,
  Divider,
  Code,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  InputGroup,
  InputLeftElement,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Textarea,
  IconButton,
} from '@chakra-ui/react';
import { FiDatabase, FiRefreshCw, FiTrash2, FiZap, FiClock, FiHardDrive, FiActivity, FiSearch, FiKey, FiEye, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

const API_BASE = '/api/v1/admin-ui';

function RedisStatus() {
  const toast = useToast();
  const [redisData, setRedisData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [flushing, setFlushing] = useState(false);
  const { isOpen: isFlushModalOpen, onOpen: onFlushModalOpen, onClose: onFlushModalClose } = useDisclosure();
  const [flushType, setFlushType] = useState('rate_limits');

  // Key browser state
  const [keyPattern, setKeyPattern] = useState('*');
  const [keyList, setKeyList] = useState([]);
  const [keyPagination, setKeyPagination] = useState({ page: 1, total: 0, total_pages: 1 });
  const [keysLoading, setKeysLoading] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [keyDetail, setKeyDetail] = useState(null);
  const [keyDetailLoading, setKeyDetailLoading] = useState(false);
  const [lookupKeyInput, setLookupKeyInput] = useState('');

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
    'X-Tenant-ID': localStorage.getItem('instance_id'),
  });

  const fetchRedisStatus = useCallback(async (showToast = false) => {
    if (showToast) setRefreshing(true);
    else setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/redis/status/`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success) {
        setRedisData(data.redis);
        if (showToast) {
          toast({ title: 'Redis status refreshed', status: 'success', duration: 2000 });
        }
      } else {
        toast({ title: 'Error loading Redis status', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error connecting to API', status: 'error', duration: 3000 });
    }

    setLoading(false);
    setRefreshing(false);
  }, [toast]);

  useEffect(() => {
    fetchRedisStatus();
  }, [fetchRedisStatus]);

  // Auto-refresh every 10 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => fetchRedisStatus(), 10000);
    return () => clearInterval(interval);
  }, [fetchRedisStatus, autoRefresh]);

  const handleFlush = async () => {
    setFlushing(true);
    try {
      const res = await fetch(`${API_BASE}/redis/flush/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ type: flushType }),
      });
      const data = await res.json();
      if (data.success) {
        toast({ title: data.message, status: 'success', duration: 3000 });
        fetchRedisStatus(true);
      } else {
        toast({ title: data.message || 'Error flushing Redis', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error flushing Redis', status: 'error', duration: 3000 });
    }
    setFlushing(false);
    onFlushModalClose();
  };

  const fetchKeys = async (page = 1, pattern = keyPattern) => {
    setKeysLoading(true);
    setSelectedKey(null);
    setKeyDetail(null);
    try {
      const params = new URLSearchParams({ pattern, page: page.toString() });
      const res = await fetch(`${API_BASE}/redis/keys/?${params}`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success) {
        setKeyList(data.keys);
        setKeyPagination(data.pagination);
      } else {
        toast({ title: data.message || 'Error listing keys', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error listing keys', status: 'error', duration: 3000 });
    }
    setKeysLoading(false);
  };

  const fetchKeyDetail = async (key) => {
    setSelectedKey(key);
    setKeyDetailLoading(true);
    try {
      const params = new URLSearchParams({ key });
      const res = await fetch(`${API_BASE}/redis/key/?${params}`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success) {
        setKeyDetail(data);
      } else {
        toast({ title: data.message || 'Key not found', status: 'warning', duration: 3000 });
        setKeyDetail(null);
      }
    } catch (e) {
      toast({ title: 'Error reading key', status: 'error', duration: 3000 });
      setKeyDetail(null);
    }
    setKeyDetailLoading(false);
  };

  const handleKeySearch = (e) => {
    e.preventDefault();
    fetchKeys(1, keyPattern);
  };

  const handleLookupKey = (e) => {
    e.preventDefault();
    if (lookupKeyInput.trim()) {
      fetchKeyDetail(lookupKeyInput.trim());
    }
  };

  const formatValue = (val) => {
    if (val === null || val === undefined) return 'null';
    if (typeof val === 'object') return JSON.stringify(val, null, 2);
    return String(val);
  };

  const formatTtl = (ttl) => {
    if (ttl === null || ttl === undefined) return 'No expiry';
    if (ttl < 60) return `${ttl}s`;
    if (ttl < 3600) return `${Math.floor(ttl / 60)}m ${ttl % 60}s`;
    return `${Math.floor(ttl / 3600)}h ${Math.floor((ttl % 3600) / 60)}m`;
  };

  const typeColor = (type) => {
    const colors = { string: 'green', list: 'blue', set: 'purple', zset: 'orange', hash: 'teal' };
    return colors[type] || 'gray';
  };

  const formatUptime = (seconds) => {
    if (!seconds) return 'N/A';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (days > 0) return `${days}d ${hours}h ${mins}m`;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  if (loading) {
    return (
      <Box textAlign="center" py={20}>
        <Spinner size="xl" color="orange.500" />
        <Text mt={4}>Loading Redis status...</Text>
      </Box>
    );
  }

  const connected = redisData?.connected;
  const info = redisData?.server_info || {};
  const memory = redisData?.memory || {};
  const keys = redisData?.key_stats || {};

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <HStack spacing={3}>
          <Heading size="lg">Redis Status</Heading>
          <Badge
            colorScheme={connected ? 'green' : 'red'}
            fontSize="sm"
            px={3}
            py={1}
            borderRadius="full"
          >
            {connected ? 'Connected' : 'Disconnected'}
          </Badge>
        </HStack>
        <HStack spacing={3}>
          <Button
            size="sm"
            variant={autoRefresh ? 'solid' : 'outline'}
            colorScheme="blue"
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
          </Button>
          <Button
            leftIcon={<FiRefreshCw />}
            colorScheme="blue"
            variant="outline"
            onClick={() => fetchRedisStatus(true)}
            isLoading={refreshing}
            size="sm"
          >
            Refresh
          </Button>
          <Button
            leftIcon={<FiTrash2 />}
            colorScheme="red"
            variant="outline"
            onClick={onFlushModalOpen}
            size="sm"
            isDisabled={!connected}
          >
            Flush Keys
          </Button>
        </HStack>
      </HStack>

      {redisData?.error && !connected && (
        <Alert status="error" borderRadius="lg" mb={6}>
          <AlertIcon />
          <Box>
            <Text fontWeight="bold">Redis Connection Error</Text>
            <Code mt={1} fontSize="sm">{redisData.error}</Code>
          </Box>
        </Alert>
      )}

      {!connected && (
        <Alert status="warning" borderRadius="lg" mb={6}>
          <AlertIcon />
          <Box>
            <Text fontWeight="bold">Cache Backend: {redisData?.backend}</Text>
            <Text fontSize="sm">Redis is not connected. Rate limiting is using a fallback cache backend.</Text>
          </Box>
        </Alert>
      )}

      {connected && (
        <Tabs colorScheme="orange">
          <TabList>
            <Tab><HStack spacing={2}><FiActivity /><Text>Overview</Text></HStack></Tab>
            <Tab><HStack spacing={2}><FiKey /><Text>Key Browser</Text></HStack></Tab>
          </TabList>
          <TabPanels>
            {/* Overview Tab */}
            <TabPanel px={0}>
        <VStack spacing={6} align="stretch">
          {/* Server Overview */}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
            <Stat bg="white" p={5} borderRadius="lg" boxShadow="sm" borderLeft="4px solid" borderColor="green.400">
              <StatLabel color="gray.500">
                <HStack spacing={1}><FiDatabase /><Text>Version</Text></HStack>
              </StatLabel>
              <StatNumber fontSize="xl" color="green.600">Redis {info.redis_version}</StatNumber>
              <StatHelpText>Port {info.tcp_port}</StatHelpText>
            </Stat>

            <Stat bg="white" p={5} borderRadius="lg" boxShadow="sm" borderLeft="4px solid" borderColor="blue.400">
              <StatLabel color="gray.500">
                <HStack spacing={1}><FiClock /><Text>Uptime</Text></HStack>
              </StatLabel>
              <StatNumber fontSize="xl" color="blue.600">{formatUptime(info.uptime_seconds)}</StatNumber>
              <StatHelpText>{info.connected_clients} connected client{info.connected_clients !== 1 ? 's' : ''}</StatHelpText>
            </Stat>

            <Stat bg="white" p={5} borderRadius="lg" boxShadow="sm" borderLeft="4px solid" borderColor="purple.400">
              <StatLabel color="gray.500">
                <HStack spacing={1}><FiZap /><Text>Latency</Text></HStack>
              </StatLabel>
              <StatNumber fontSize="xl" color="purple.600">{info.latency_ms} ms</StatNumber>
              <StatHelpText>Ping round-trip</StatHelpText>
            </Stat>

            <Stat bg="white" p={5} borderRadius="lg" boxShadow="sm" borderLeft="4px solid" borderColor="orange.400">
              <StatLabel color="gray.500">
                <HStack spacing={1}><FiHardDrive /><Text>Memory Used</Text></HStack>
              </StatLabel>
              <StatNumber fontSize="xl" color="orange.600">{memory.used_memory_human}</StatNumber>
              <StatHelpText>Peak: {memory.used_memory_peak_human}</StatHelpText>
            </Stat>
          </SimpleGrid>

          {/* Key Statistics */}
          <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
            <HStack mb={4}>
              <FiActivity />
              <Heading size="md">Key Statistics</Heading>
            </HStack>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
              <Box p={4} bg="gray.50" borderRadius="md">
                <Text fontSize="sm" color="gray.500" mb={1}>Total Keys</Text>
                <Text fontSize="2xl" fontWeight="bold">{keys.total_keys?.toLocaleString()}</Text>
              </Box>
              <Box p={4} bg="orange.50" borderRadius="md">
                <Text fontSize="sm" color="gray.500" mb={1}>Rate Limit Keys</Text>
                <Text fontSize="2xl" fontWeight="bold" color="orange.600">{keys.rate_limit_keys?.toLocaleString()}</Text>
              </Box>
              <Box p={4} bg="blue.50" borderRadius="md">
                <Text fontSize="sm" color="gray.500" mb={1}>Throttle Keys</Text>
                <Text fontSize="2xl" fontWeight="bold" color="blue.600">{keys.throttle_keys?.toLocaleString()}</Text>
              </Box>
            </SimpleGrid>
          </Box>

          {/* Cache Performance */}
          <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
            <Heading size="md" mb={4}>Cache Performance</Heading>
            <VStack spacing={4} align="stretch">
              <Box>
                <HStack justify="space-between" mb={1}>
                  <Text fontSize="sm" fontWeight="medium">Hit Rate</Text>
                  <Text fontSize="sm" color="gray.500">{keys.hit_rate}%</Text>
                </HStack>
                <Progress
                  value={keys.hit_rate || 0}
                  colorScheme={keys.hit_rate >= 90 ? 'green' : keys.hit_rate >= 70 ? 'yellow' : 'red'}
                  borderRadius="full"
                  size="sm"
                />
              </Box>
              <Divider />
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>Metric</Th>
                    <Th isNumeric>Value</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td>Cache Hits</Td>
                    <Td isNumeric>{keys.hits?.toLocaleString()}</Td>
                  </Tr>
                  <Tr>
                    <Td>Cache Misses</Td>
                    <Td isNumeric>{keys.misses?.toLocaleString()}</Td>
                  </Tr>
                  <Tr>
                    <Td>Expired Keys</Td>
                    <Td isNumeric>{keys.expired_keys?.toLocaleString()}</Td>
                  </Tr>
                  <Tr>
                    <Td>Evicted Keys</Td>
                    <Td isNumeric>{keys.evicted_keys?.toLocaleString()}</Td>
                  </Tr>
                </Tbody>
              </Table>
            </VStack>
          </Box>

          {/* Memory Details */}
          <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
            <Heading size="md" mb={4}>Memory Details</Heading>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Property</Th>
                  <Th>Value</Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <Td>Used Memory</Td>
                  <Td><Code>{memory.used_memory_human}</Code></Td>
                </Tr>
                <Tr>
                  <Td>Peak Memory</Td>
                  <Td><Code>{memory.used_memory_peak_human}</Code></Td>
                </Tr>
                <Tr>
                  <Td>Max Memory</Td>
                  <Td><Code>{memory.maxmemory_human || 'Unlimited'}</Code></Td>
                </Tr>
                <Tr>
                  <Td>Eviction Policy</Td>
                  <Td><Badge colorScheme="gray">{memory.maxmemory_policy}</Badge></Td>
                </Tr>
                <Tr>
                  <Td>Fragmentation Ratio</Td>
                  <Td>
                    <Badge colorScheme={memory.mem_fragmentation_ratio > 1.5 ? 'red' : 'green'}>
                      {memory.mem_fragmentation_ratio}
                    </Badge>
                  </Td>
                </Tr>
                <Tr>
                  <Td>OS</Td>
                  <Td><Code fontSize="xs">{info.os}</Code></Td>
                </Tr>
              </Tbody>
            </Table>
          </Box>
        </VStack>
            </TabPanel>

            {/* Key Browser Tab */}
            <TabPanel px={0}>
              <VStack spacing={6} align="stretch">
                {/* Search / Filter */}
                <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
                  <Heading size="md" mb={4}>Browse Keys</Heading>
                  <form onSubmit={handleKeySearch}>
                    <HStack mb={4}>
                      <InputGroup>
                        <InputLeftElement pointerEvents="none"><FiSearch color="gray" /></InputLeftElement>
                        <Input
                          placeholder="Pattern (e.g. rate_limit:* or *)"
                          value={keyPattern}
                          onChange={(e) => setKeyPattern(e.target.value)}
                          focusBorderColor="orange.500"
                        />
                      </InputGroup>
                      <Button type="submit" colorScheme="orange" isLoading={keysLoading} px={8}>
                        Search
                      </Button>
                    </HStack>
                  </form>

                  {/* Direct key lookup */}
                  <form onSubmit={handleLookupKey}>
                    <HStack>
                      <InputGroup>
                        <InputLeftElement pointerEvents="none"><FiEye color="gray" /></InputLeftElement>
                        <Input
                          placeholder="View specific key (exact name)"
                          value={lookupKeyInput}
                          onChange={(e) => setLookupKeyInput(e.target.value)}
                          focusBorderColor="blue.500"
                        />
                      </InputGroup>
                      <Button type="submit" colorScheme="blue" isLoading={keyDetailLoading} px={8}>
                        View
                      </Button>
                    </HStack>
                  </form>
                </Box>

                {/* Key Detail Panel */}
                {(keyDetail || keyDetailLoading) && (
                  <Box bg="white" p={6} borderRadius="lg" boxShadow="sm" borderLeft="4px solid" borderColor="blue.400">
                    {keyDetailLoading ? (
                      <HStack><Spinner size="sm" /><Text>Loading key...</Text></HStack>
                    ) : keyDetail && (
                      <>
                        <HStack justify="space-between" mb={3}>
                          <HStack spacing={3}>
                            <Code fontSize="md" fontWeight="bold">{keyDetail.key}</Code>
                            <Badge colorScheme={typeColor(keyDetail.type)}>{keyDetail.type}</Badge>
                            <Badge colorScheme={keyDetail.ttl !== null ? 'yellow' : 'gray'}>
                              {formatTtl(keyDetail.ttl)}
                            </Badge>
                          </HStack>
                          <Button size="xs" variant="ghost" onClick={() => { setKeyDetail(null); setSelectedKey(null); }}>
                            Close
                          </Button>
                        </HStack>
                        <Textarea
                          value={formatValue(keyDetail.value)}
                          isReadOnly
                          fontFamily="mono"
                          fontSize="sm"
                          bg="gray.50"
                          minH="120px"
                          maxH="400px"
                          resize="vertical"
                        />
                      </>
                    )}
                  </Box>
                )}

                {/* Key List Table */}
                {keyList.length > 0 && (
                  <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden">
                    <HStack justify="space-between" p={4} bg="gray.50" borderBottom="1px" borderColor="gray.200">
                      <Text fontSize="sm" color="gray.600">
                        {keyPagination.total.toLocaleString()} key{keyPagination.total !== 1 ? 's' : ''} found
                      </Text>
                      <HStack spacing={2}>
                        <IconButton
                          icon={<FiChevronLeft />}
                          size="sm"
                          variant="outline"
                          isDisabled={keyPagination.page <= 1}
                          onClick={() => fetchKeys(keyPagination.page - 1)}
                          aria-label="Previous page"
                        />
                        <Text fontSize="sm" color="gray.600" minW="80px" textAlign="center">
                          Page {keyPagination.page} / {keyPagination.total_pages}
                        </Text>
                        <IconButton
                          icon={<FiChevronRight />}
                          size="sm"
                          variant="outline"
                          isDisabled={keyPagination.page >= keyPagination.total_pages}
                          onClick={() => fetchKeys(keyPagination.page + 1)}
                          aria-label="Next page"
                        />
                      </HStack>
                    </HStack>
                    <Table size="sm">
                      <Thead>
                        <Tr>
                          <Th>Key</Th>
                          <Th width="100px">Type</Th>
                          <Th width="120px">TTL</Th>
                          <Th width="80px">Action</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {keyList.map((k) => (
                          <Tr
                            key={k.key}
                            bg={selectedKey === k.key ? 'blue.50' : undefined}
                            _hover={{ bg: 'gray.50' }}
                            cursor="pointer"
                            onClick={() => fetchKeyDetail(k.key)}
                          >
                            <Td>
                              <Code fontSize="xs">{k.key}</Code>
                            </Td>
                            <Td>
                              <Badge colorScheme={typeColor(k.type)} fontSize="xs">{k.type}</Badge>
                            </Td>
                            <Td>
                              <Text fontSize="xs" color="gray.500">{formatTtl(k.ttl)}</Text>
                            </Td>
                            <Td>
                              <IconButton
                                icon={<FiEye />}
                                size="xs"
                                variant="ghost"
                                colorScheme="blue"
                                onClick={(e) => { e.stopPropagation(); fetchKeyDetail(k.key); }}
                                aria-label="View key"
                              />
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                    {keyPagination.total_pages > 1 && (
                      <HStack justify="center" p={4} bg="gray.50" borderTop="1px" borderColor="gray.200">
                        <IconButton
                          icon={<FiChevronLeft />}
                          size="sm"
                          variant="outline"
                          isDisabled={keyPagination.page <= 1}
                          onClick={() => fetchKeys(keyPagination.page - 1)}
                          aria-label="Previous page"
                        />
                        <Text fontSize="sm" color="gray.600">
                          Page {keyPagination.page} / {keyPagination.total_pages}
                        </Text>
                        <IconButton
                          icon={<FiChevronRight />}
                          size="sm"
                          variant="outline"
                          isDisabled={keyPagination.page >= keyPagination.total_pages}
                          onClick={() => fetchKeys(keyPagination.page + 1)}
                          aria-label="Next page"
                        />
                      </HStack>
                    )}
                  </Box>
                )}

                {keyList.length === 0 && !keysLoading && keyPagination.total === 0 && (
                  <Box bg="white" p={8} borderRadius="lg" boxShadow="sm" textAlign="center">
                    <Text color="gray.500">Use the search above to browse Redis keys</Text>
                  </Box>
                )}
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>
      )}

      {/* Flush Confirmation Modal */}
      <Modal isOpen={isFlushModalOpen} onClose={onFlushModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Flush Redis Keys</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">This action cannot be undone. Choose what to flush:</Text>
              </Alert>
              <Button
                variant={flushType === 'rate_limits' ? 'solid' : 'outline'}
                colorScheme="orange"
                onClick={() => setFlushType('rate_limits')}
                justifyContent="flex-start"
                py={6}
              >
                <Box textAlign="left">
                  <Text fontWeight="bold">Rate Limit Keys Only</Text>
                  <Text fontSize="xs" fontWeight="normal">Clears rate_limit:* and throttle_* keys. Safe for production.</Text>
                </Box>
              </Button>
              <Button
                variant={flushType === 'all' ? 'solid' : 'outline'}
                colorScheme="red"
                onClick={() => setFlushType('all')}
                justifyContent="flex-start"
                py={6}
              >
                <Box textAlign="left">
                  <Text fontWeight="bold">All Keys (Flush DB)</Text>
                  <Text fontSize="xs" fontWeight="normal">Removes ALL keys from the database. Use with caution.</Text>
                </Box>
              </Button>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onFlushModalClose}>Cancel</Button>
            <Button
              colorScheme={flushType === 'all' ? 'red' : 'orange'}
              onClick={handleFlush}
              isLoading={flushing}
            >
              Flush {flushType === 'all' ? 'All Keys' : 'Rate Limit Keys'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default RedisStatus;
